import {
    DOCUMENT_TYPE,
    ERROR_CATEGORIES,
    ROW_TYPE,
} from 'Config/authenticated/dictionary/docForm/docFormEnums';
import {
    fetchPaymentList,
    getAllDocumentCustomFields,
    getAllDocumentCustomFieldsValues,
    linkPayments,
    updateDocumentCustomFields,
} from 'api/document/documentService';
import { isEqual } from 'lodash';
import { formatCustomFieldsPayload } from 'Helpers/customFields/customFieldsHelpers';
import { CUSTOM_FIELDS_TYPES } from 'Config/authenticated/dictionary/customFields/customFieldsEnums';
import {
    createIndividualAddress,
    getAllIndividualAddresses,
    updateIndividualAddress,
} from 'api/individuals';
import CompaniesService from 'api/companies';
import { getAllContactsForCompanyOrIndividual } from 'api/contacts/contactsService';
import { DocFormValidationError } from 'errors/DocFormValidationError';
import { CustomFieldValidationError } from 'errors/CustomFieldValidationError';
import { defineStore } from 'pinia';
import { logError } from 'Helpers/document/docFormHelper';
import Big from 'big.js';

export const useDocumentFormStore = defineStore('documentForm', {
    state: () => ({
        docId: null,
        rows: [],
        taxes: [],
        lawTexts: {
            tvaLawText: '',
            penaltyLateWarningText: '',
            tvaLawTextChecked: false,
            penaltyLateWarningTextChecked: false,
        },
        documentData: {
            date: '',
            companyReference: '',
            number: '',
            orderReference: '',
            subject: '',
            note: '',
            rateCategory: null,
            rateCategoryId: null,
            layoutId: null,
            langId: null,
            isTaxesInc: null,
            vatMode: null,
            vatMention: null,
            paymentMethods: [],
            paymentTerms: null,
            settings: {
                payments: {
                    paymentModules: [],
                    directDebitModule: null,
                },
                pdfDisplay: null,
                shipping: {
                    shippingWeight: null,
                    shippingVolume: null,
                    shippingDate: null,
                },
                assignedStaffLabelId: null,
            },
            _embed: {
                paymentTerms: null,
            },
            bankAccountId: null,
            checkLabelId: null,
            related: null,
            analyticCode: null,
            deliveryAddressId: null,
            parent: null,
        },
        recipient: {
            address: null,
            contact: null,
            name: '',
        },
        sender: null,
        config: {
            labelDocNumber: null,
            drawerSettings: null,
        },
        activeButton: null,
        isCalculating: false,
        isSettingsLoading: false,
        isLoading: true,
        paymentsToLink: [],
        customFields: {
            values: [],
            initialValues: [],
        },
        metadata: {
            settings: null,
            assignedStaffLabels: [],
            recipientAddresses: [],
            contacts: [],
            civilities: [],
            paymentsList: [],
            accountingCodesList: [],
            shippingAccountingCodesList: [],
            numberFormat: null,
            lastValidationDate: null,
            permissions: {
                add_single_lines: false,
                list_payments: false,
                validate_invoice: false,
                can_edit_date: false,
                can_edit_identifier: false,
            },
            analytics: null,
        },
        currentDueAmount: '0', // Montant total net après application des paiements liés
        documentDueAmount: '0', // Montant total net du document venant l'api
        createDocument: null,
        updateDocument: null,
        validationErrors: [],
        temporary: {
            // This object is used to store temporary data that should not be sent to the API or saved in the state
        },
    }),
    getters: {
        getNumberProductsDoc: (state) => {
            return state.rows
                .filter((row) =>
                    [
                        ROW_TYPE.SINGLE,
                        ROW_TYPE.SHIPPING,
                        ROW_TYPE.PACKAGING,
                        ROW_TYPE.CATALOG,
                    ].includes(row.type),
                )
                .reduce((acc, product) => {
                    return acc + Number(product.quantity);
                }, 0);
        },
        isUpdate: (state) => state.docId !== null,
        getDocType: (state) => {
            return state.docId ? DOCUMENT_TYPE.INVOICE : state.documentData.parent?.type;
        },
        getThirdId: (state) => {
            return state.documentData.related?.find(
                (item) => item.type === 'company' || item.type === 'individual',
            )?.id;
        },
        getClientIsIndividual: (state) => {
            return state.documentData.related.find((item) => item.type === 'individual');
        },
        getRowById: (state) => (id) => {
            return state.rows.find((row) => row.id === id);
        },
        getTaxesFilteredByAmount: (state) => {
            return state.taxes?.filter((tax) => Big(tax.amount).gt(0)) ?? [];
        },
        hasPaymentOrShippingErrors: (state) => {
            return state.validationErrors.some(
                (error) =>
                    error.type === ERROR_CATEGORIES.PAYMENT_SETTINGS ||
                    error.type === ERROR_CATEGORIES.SHIPPING_SETTINGS,
            );
        },
    },
    actions: {
        async initStore(data) {
            const { docId, ...documentData } = data.documentData;

            this.rows = data.rows;
            this.taxes = data.taxes;
            this.documentData = {
                ...this.documentData,
                ...documentData,
            };
            this.recipient = data.recipient;
            this.sender = data.sender;
            this.config = data.config;
            this.lawTexts = data.lawTexts;

            // These functions are set in the parent component because they differ based on the document type (invoice, order, estimate)
            this.createDocument = data.createDocument;
            this.updateDocument = data.updateDocument;

            if (docId) {
                this.docId = docId;
            }
            await this.getCustomFieldsListAndValues();

            this.metadata = {
                ...data.metadata,
                recipientAddresses: await this.fetchRecipientAddresses(),
                contacts: await this.fetchContacts(),
                paymentsList: [],
            };

            if (this.metadata.permissions?.list_payments) {
                this.metadata.paymentsList = await fetchPaymentList(
                    this.getDocType,
                    this.documentData.parent.id,
                );
            }
            this.temporary.paymentTerms = { ...this.documentData.paymentTerms };
        },
        updateRow({ value, id, triggerCompute = true }) {
            let updatedRow = null;
            this.rows = this.rows.map((row) => {
                if (row.id === id) {
                    updatedRow = { ...row, ...value };
                    return updatedRow;
                }
                return row;
            });

            if (updatedRow) {
                EventBus.emit('docForm:row-updated', { row: updatedRow, triggerCompute });
            }
        },
        updateRowByUuid(value, uuid, triggerCompute = true) {
            let updatedRow = null;
            this.rows = this.rows.map((row) => {
                if (row.uuid === uuid) {
                    updatedRow = { ...row, ...value };
                    return updatedRow;
                }
                return row;
            });
            if (updatedRow) {
                EventBus.emit('docForm:row-updated', { row: updatedRow, triggerCompute });
            }
        },

        async saveDocument(additionalPayload = {}) {
            await this.validateCustomFields();
            if (this.validationErrors.length) {
                window.sellsyapp.config.globalProperties.$store.dispatch('popupPanel/open', {
                    key: 'docFormErrorDialog',
                });
                throw new DocFormValidationError();
            }
            const payload = {
                ...additionalPayload,
                documentData: {
                    ...this.documentData,
                    langId: this.documentData.langId === 0 ? null : this.documentData.langId,
                },
                rows: this.rows,
                recipient: this.recipient,
                sender: this.sender,
            };
            const { data } = this.isUpdate
                ? await this.updateDocument({ ...payload, id: this.docId })
                : await this.createDocument(payload);

            this.docId = data.id;
            if (this.config?.drawerSettings?.customFields) {
                await this.saveCustomFieldsValues();
            }
            window.sellsyapp.config.globalProperties.$pushToast({
                type: 'success',
                text: window.sellsyapp.config.globalProperties.$t.__(
                    'document.DocFormLayout.success_save_draft',
                ),
                unique: true,
            });
            return data;
        },
        async getCustomFieldsListAndValues() {
            const docId = this.docId ?? this.documentData.parent.id;

            const documentCustomFieldValues = await getAllDocumentCustomFieldsValues(
                this.getDocType,
                docId,
            );

            const customFieldsList = await getAllDocumentCustomFields();

            this.metadata = {
                ...this.metadata,
                settings: {
                    ...this.metadata.settings,
                    customFieldsList,
                },
            };

            const mergedArray = customFieldsList.map((customField) => {
                const documentCustomFieldValue = documentCustomFieldValues?.find(
                    (documentCustomFieldValue) => documentCustomFieldValue.id === customField.id,
                );
                if (
                    documentCustomFieldValue &&
                    documentCustomFieldValue.type === CUSTOM_FIELDS_TYPES.THIRD
                ) {
                    documentCustomFieldValue.value =
                        documentCustomFieldValue.value?.map(({ id }) => id) ?? null;
                }
                return Object.assign({}, customField, documentCustomFieldValue ?? {}, {
                    error: '',
                });
            }, []);

            this.customFields = {
                initialValues: JSON.parse(JSON.stringify(mergedArray)),
                values: mergedArray,
            };
        },
        async checkHasCustomFieldUpdated(initialCustomFieldsValues) {
            return initialCustomFieldsValues.some((initialValue) => {
                const updatedValue = this.customFields.values.find(
                    (value) => value.id === initialValue.id,
                );

                if (!updatedValue) {
                    return false;
                }

                const mandatoryButEmpty = updatedValue.mandatory && updatedValue.value === '';

                return (
                    !isEqual(updatedValue.value, initialValue.value) || mandatoryButEmpty // check if the field is mandatory and empty
                );
            });
        },
        async validateCustomFields(throwError = false) {
            try {
                const docType = this.getDocType;
                let endpoint;
                const id = this.docId ?? this.documentData.parent.id;
                switch (docType) {
                    case DOCUMENT_TYPE.INVOICE:
                    case DOCUMENT_TYPE.PROFORMA:
                    case DOCUMENT_TYPE.DEPOSIT_INVOICE:
                        endpoint = `/invoices/${id}/custom-fields`;
                        break;
                    case DOCUMENT_TYPE.ORDER:
                        endpoint = `/orders/${id}/custom-fields`;
                        break;
                    case DOCUMENT_TYPE.ESTIMATE:
                    default:
                        endpoint = `/estimates/${id}/custom-fields`;
                        break;
                }
                const payload = formatCustomFieldsPayload(this.customFields.values);
                await ApiClient.put(
                    endpoint,
                    {
                        verify: true,
                    },
                    payload,
                );
                return true;
            } catch (e) {
                const error = e.response?.data?.error;
                if (error?.code === 400 && error?.details) {
                    const listIdCustomFieldInError = Object.keys(error.details);
                    for (const id of listIdCustomFieldInError) {
                        const cf = this.customFields.values.find(
                            (customField) => customField.id === Number(id),
                        );
                        cf.error = error.details[id];
                    }

                    if (throwError) {
                        throw new CustomFieldValidationError(
                            window.sellsyapp.config.globalProperties.$t.__(
                                'pages.invoice.ProgressInvoiceFormPage.custom_fields_global_error',
                            ),
                        );
                    }

                    this.validationErrors = [
                        ...this.validationErrors,
                        {
                            type: ERROR_CATEGORIES.CUSTOMFIELDS,
                            errors: Object.values(error.details).flat(),
                        },
                    ];
                }
            }
        },
        resetCustomFieldsValues(initialCustomFieldValues) {
            for (const customFieldValue of this.customFields.values) {
                const initialCustomFieldValue = initialCustomFieldValues.find(
                    (initialValue) => initialValue.id === customFieldValue.id,
                );
                if (initialCustomFieldValue) {
                    customFieldValue.value = initialCustomFieldValue.value;
                    customFieldValue.error = '';
                }
            }
        },
        async saveCustomFieldsValues() {
            if (!(await this.checkHasCustomFieldUpdated(this.customFields.initialValues))) {
                return;
            }

            await updateDocumentCustomFields(
                this.docId,
                formatCustomFieldsPayload(this.customFields.values),
            );
        },
        async fetchRecipientAddresses() {
            let addresses = [];
            try {
                if (!this.getThirdId) {
                    return;
                } else if (this.getClientIsIndividual) {
                    addresses = await getAllIndividualAddresses(this.getThirdId);
                } else {
                    addresses = await CompaniesService.getAllCompanyAddresses(this.getThirdId);
                }

                this.recipient.hasAddress = addresses.length > 0;

                if (
                    addresses.findIndex((address) => address.id === this.recipient.address?.id) ===
                    -1
                ) {
                    this.recipient.address = null;
                }

                if (
                    addresses.findIndex(
                        (address) => address.id === this.documentData.deliveryAddressId,
                    ) === -1
                ) {
                    this.documentData.deliveryAddressId = null;
                }
            } catch (e) {
                if (e.response?.status !== 403) {
                    logError(e);
                }
            }

            return addresses;
        },
        async createDeliveryAddress(address) {
            let response = null;
            if (this.getClientIsIndividual) {
                response = (await createIndividualAddress(this.getThirdId, address)).data;
            } else {
                response = (await CompaniesService.createCompanyAddress(this.getThirdId, address))
                    .data;
            }
            this.metadata.recipientAddresses = this.metadata.recipientAddresses.concat(response);
            return response;
        },
        async updateDeliveryAddress(address) {
            let response = null;
            if (this.getClientIsIndividual) {
                response = (await updateIndividualAddress(this.getThirdId, address)).data;
            } else {
                response = (await CompaniesService.updateCompanyAddress(this.getThirdId, address))
                    .data;
            }
            this.metadata.recipientAddresses = this.metadata.recipientAddresses.map((item) =>
                item.id === response.id ? response : item,
            );
            return response;
        },
        fetchContacts() {
            if (!this.getThirdId) {
                return [];
            }

            return getAllContactsForCompanyOrIndividual({
                isIndividual: false,
                id: this.getThirdId,
            });
        },
        async savePaymentsToLink() {
            try {
                //split array by 10 because batch request is limited to 20 request max
                for (let i = 0; i < this.paymentsToLink.length; i += 10) {
                    await linkPayments({
                        parentDocType: this.documentData.parent.type,
                        parentDocId: this.documentData.parent.id,
                        docType: DOCUMENT_TYPE.INVOICE,
                        docId: this.docId,
                        payments: this.paymentsToLink.slice(i, i + 10),
                    });
                }
            } catch (e) {
                logError(e);
            }
        },
    },
});
